import { useEffect, useRef, useState } from 'react';
import { createTrialClock, afterPaint as defaultAfterPaint } from './trialClock';
import { makeEventId } from './eventId';

// The server rejects reaction times below this as implausible, and responses
// this fast are anticipations rather than reactions. Ignoring them keeps us
// from ever building a payload the server will refuse.
const MIN_RT_MS = 100;

// Bound by the task's OWN response window, not a global constant. The server
// derives its ceiling the same way (ai-learn-api 867a885), so anything inside
// the window is accepted — tower-of-london legitimately runs to 60s and
// corsi-span to 20s, both past the fixed 30s a global constant would impose.
// A value beyond the window (plus this grace) means a runaway timer or a
// backgrounded tab kept counting, not a real response; such a trial is
// recorded as a NON-response — all three fields null — which is a valid datum
// the server grades as incorrect. Submitting the out-of-range value instead
// would 400 the entire block and lose every other trial in it. Falls back to
// 30000ms only when a task declares no window of its own.
const RT_GRACE_MS = 250;
const maxRtMsFor = (active) => (active.timing?.windowMs ?? 30000) + RT_GRACE_MS;

const RETRY_DELAYS_MS = [400, 1200, 3000];

// A 400 means we built a malformed payload — wrong trial count, duplicate or
// missing indices, a partial response trio, an out-of-range reaction time, or
// a session served under a superseded ladder/administration version. Retrying
// sends the same broken body again and hides the bug. 403 is auth or
// subscription; 404 is an unknown run or session. None of the three changes
// with time.
const FATAL_STATUSES = [400, 403, 404];

// A task may declare that a given trial accepts no response at all — a
// speed-match prime, for instance, is shown only to establish the card the
// next trial is compared against. Such a trial still has a record and is
// still submitted; it just carries nulls, which the server grades as
// undefined rather than wrong. Absent means every trial is responsive.
const acceptsResponse = (task, trial) =>
  typeof task.acceptsResponse === 'function' ? task.acceptsResponse(trial) : true;

// Feedback needs two independent permissions. The driver decides whether a
// scored trial may show it at all (training yes, assessment never). The task
// decides whether correctness is computable on the client — rotation
// deliberately withholds its answer, so no amount of driver permission can
// produce one.
const feedbackAllowed = (task, driver, trial) =>
  task.feedbackAvailable !== false &&
  (trial.isPractice || driver.feedbackOnEveryTrial === true);

// Context a task may need to judge its own correctness for PRACTICE feedback.
// Everything here is already on screen or already served — none of it is a
// withheld answer. blockTrials is the whole served block, which n-back needs:
// its rule is "does this letter match the one n back", and the stream is served
// in full, so the answer is derivable from what the participant already saw.
const expectationContext = (blk, i) => ({
  trial: blk.trials[i],
  params: blk.params,
  prevStimulus: i > 0 ? blk.trials[i - 1].stimulus : null,
  blockTrials: blk.trials,
});

// A composite trial that stamped an RT via beginComposite but was never
// committed carries a half-built record: `answer` null, `reactionTimeMs`
// set. That is exactly the partial trio the server 400s the whole block
// for. Every path that lets go of a live trial without a commit — the
// response window timing out, the tab hiding, the run being abandoned
// outright — must roll the record back to a clean, all-null omission
// before it releases `active`. Centralised here so there is exactly one
// place that decides what "abandoned mid-composite" means.
const rollbackOpenComposite = (active) => {
  if (active && active.record.answer === null && active.record.reactionTimeMs !== null) {
    active.record.reactionTimeMs = null;
    active.record.respondedAtEpochMs = null;
  }
};

export function useTrialRunner({
  driver,
  task,
  createClock = createTrialClock,
  afterPaint = defaultAfterPaint,
}) {
  const [phase, setPhase] = useState('idle');
  const [stage, setStage] = useState('gap');
  const [block, setBlock] = useState(null);
  const [blockCount, setBlockCount] = useState(0);
  const [trialIndex, setTrialIndex] = useState(-1);
  const [blockStats, setBlockStats] = useState([]);
  const [finalResult, setFinalResult] = useState(null);
  // Training-only: the final block's response carries a session-level
  // summary (entry/exit level, accuracy, mean RT — see trainingDriver.js).
  // Assessment's submitBlock never sets this field, so it stays null there,
  // same as it does for every non-final training block.
  const [sessionSummary, setSessionSummary] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [selectedTargetId, setSelectedTargetId] = useState(null);
  const [error, setError] = useState(null);
  // The phase descriptor currently on screen during stage === 'preamble', or
  // null. delayed-recognition's study list and filled delay are the first
  // consumers — see phasesBefore/runPhases below.
  const [preamblePhase, setPreamblePhase] = useState(null);

  const clockRef = useRef(null);
  const blockRef = useRef(null);
  const responsesRef = useRef([]);
  const activeRef = useRef(null);
  const timersRef = useRef([]);
  const phaseRef = useRef('idle');
  // Set when a response is accepted; read by the next trial's onset when the
  // task chains. Cleared at the start of every block so a chained task cannot
  // carry an onset across a block boundary.
  const lastResponsePerfRef = useRef(null);
  // Monotonic run id. Every deferred callback captures the value it was created
  // under and does nothing if it no longer matches, so a callback that outlives
  // its run is inert whether or not it could be cancelled.
  const generationRef = useRef(0);
  // Liveness is deliberately separate from the generation counter, because the
  // two answer different questions. The generation catches "stale but still
  // alive" (pause, restart). It cannot catch "the hook is gone": startBlock()
  // legitimately bumps the generation itself, so a continuation resuming after
  // an await mints a fresh generation that its own callbacks then match, and the
  // loop re-arms on an unmounted hook. Every await in this file is followed by a
  // check of this flag.
  const aliveRef = useRef(true);

  // The loop functions call each other, so they live on a ref that is
  // reassigned every render. This avoids both stale closures and the circular
  // dependency arrays that useCallback would require.
  const fns = useRef({});

  const setPhaseTracked = (next) => {
    phaseRef.current = next;
    setPhase(next);
  };

  // Bumping the generation is the part that actually stops the loop. Clearing
  // the setTimeout handles is not enough on its own: the double
  // requestAnimationFrame inside afterPaint has no handle we hold, so on unmount
  // or tab-hide it would still fire, push a trial record, and re-arm the timer
  // list after the only cleanup had already run.
  const clearTimers = () => {
    generationRef.current += 1;
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  // Returns the timeout id so a caller can cancel one specific timer without
  // disturbing the rest. The id is still tracked in timersRef, so a cancelled
  // timer is simply cleared twice — clearTimeout on a stale id is a no-op.
  const schedule = (fn, ms) => {
    const gen = generationRef.current;
    const id = setTimeout(() => {
      if (generationRef.current !== gen) return;
      fn();
    }, ms);
    timersRef.current.push(id);
    return id;
  };

  // Phases that run before a given trial index. A block preamble is
  // preambleBefore(0) unioned with preamble(); most tasks have neither.
  const phasesBefore = (blk, i) => {
    const fromGeneral = i === 0 && typeof task.preamble === 'function'
      ? task.preamble(blk.params, blk.trials) || []
      : [];
    const fromSpecific = typeof task.preambleBefore === 'function'
      ? task.preambleBefore(i, blk.params, blk.trials) || []
      : [];
    return [...fromGeneral, ...fromSpecific];
  };

  // Runs the queue, then the trial. Each phase is a fixed wall-clock duration:
  // delayed-recognition's 20-second filled delay is a MEASUREMENT PARAMETER, not
  // a loading spinner. Shortening it turns recognition memory into short-term
  // recall and the resulting score looks entirely normal.
  // `doneCount` is how many phases of THIS trial's queue have already run to
  // completion. It is recorded only when a phase's full duration has actually
  // elapsed — never when one merely starts — which is what lets an interrupted
  // phase be told apart from a completed one on resume. See runTrial.
  fns.current.runPhases = (queue, trialIdx, doneCount, then) => {
    if (queue.length === 0) {
      setPreamblePhase(null);
      then();
      return;
    }
    const [head, ...rest] = queue;
    setStage('preamble');
    setPreamblePhase(head);
    schedule(() => {
      // Reached only when this phase ran its full declared duration: a tab
      // hide calls clearTimers(), which bumps the generation and makes this
      // callback inert, so an interrupted phase is never counted as done.
      const done = (blockRef.current.__phasesRunFor ||= new Map());
      done.set(trialIdx, doneCount + 1);
      fns.current.runPhases(rest, trialIdx, doneCount + 1, then);
    }, head.durationMs);
  };

  fns.current.runTrial = (i) => {
    const blk = blockRef.current;
    if (!blk) return;
    if (i >= blk.trials.length) {
      fns.current.finishBlock();
      return;
    }

    const queue = phasesBefore(blk, i);
    // CONTROLLER RULING 8/10. Progress is tracked WITHIN the queue, not
    // wholesale. runTrial is re-entered on resume() after a tab hide, and the
    // two states a phase can be in at that moment are different things:
    //
    //   COMPLETED — already counted below. Never re-runs. Re-showing a
    //     20-second delay the participant already sat through would be
    //     indistinguishable from a hang. This is the guarantee that carries the
    //     real harm and it is preserved exactly.
    //   INTERRUPTED — not counted, so it re-enters from its start and the rest
    //     of the queue plays out behind it. Marking the WHOLE queue as run when
    //     the FIRST phase started (the previous behaviour) meant a learner who
    //     tab-hid during delayed-recognition's study list came back to find the
    //     20-second retention interval skipped entirely — tested immediately,
    //     memory score inflated, and nothing server-side able to detect it.
    //     Slightly more study exposure than intended is the accepted, far
    //     lesser distortion.
    const completed = blk.__phasesRunFor?.get(i) ?? 0;
    if (queue.length > completed) {
      // A preamble sits in wall-clock time between trials. lastResponsePerfRef
      // anchors a chained task's next onset to the tap that ended the
      // PREVIOUS trial; carrying that anchor across a multi-second (here,
      // possibly a 20-second filled-delay) phase would bill the whole phase
      // as this trial's reaction time. This mirrors the same clear at a block
      // boundary (startBlock) and at an interruption (visibilitychange) — a
      // preamble is a third kind of gap the onset must not survive.
      lastResponsePerfRef.current = null;
      fns.current.runPhases(queue.slice(completed), i, completed, () => fns.current.runTrial(i));
      return;
    }
    setPreamblePhase(null);

    const trial = blk.trials[i];
    // The second argument is new. Timing is per-trial on two instruments:
    // corsi-span's presentation runs for `length × presentation_interval_ms`
    // and `length` varies across the run on a fixed schedule, and n-back's
    // response window IS `isi_ms`, which the server enforces when grading.
    // Single-argument implementations are unaffected.
    const timing = task.timing(blk.params, trial);
    const chained = task.chainOnset === true;
    // A chained task shows ONE field for the whole block. A fixation cross or an
    // inter-trial gap would blink the field and turn a sequenced search into a
    // series of unrelated ones — and the server measures each step from the
    // PREVIOUS tap, so the dead time would land on one side of the interval only.
    const fixationMs = chained ? 0 : timing.fixationMs;
    // True when this trial begins in the SAME synchronous turn as the previous
    // trial's end, with no timer between them. Two decisions below hinge on it —
    // whether a fixation stage is entered, and whether the previous trial's
    // feedback may be cleared — and they must never drift apart, because they
    // are the same fact about the same tick. See both uses.
    const continuesPreviousTurn = (chained && i > 0) || fixationMs === 0;

    setTrialIndex(i);
    // A zero-length fixation is NOT a stage, and entering one anyway is
    // visible. RunnerShell unmounts the stimulus and paints a '+' whenever the
    // stage is 'fixation', so a task declaring fixationMs 0 — n-back, which
    // pairs it with gapMs 0 because its letters are a continuous stream —
    // depended on React's scheduler beating a 0ms timer to avoid flashing a
    // cross between every letter. That race is not something to rest an
    // instrument on: a blank between letters changes the maintenance demand
    // being measured, and on a repeat trial it is precisely the signal the
    // onset cue exists to supply. Skipped here, and the trial goes straight to
    // its stimulus, exactly as a chained task already did.
    if (fixationMs > 0) setStage('fixation');
    setSelectedTargetId(null);
    activeRef.current = null;
    // Practice feedback must not survive into a scored trial: the "Practice —
    // feedback on" banner switches off at this boundary, so feedback still on
    // screen during the first scored fixation reads as feedback about a scored
    // response. Under a driver that feeds back on every trial there is no such
    // boundary, and clearing here would wipe the signal before the gap.
    if (!trial.isPractice && driver.feedbackOnEveryTrial !== true) setFeedback(null);

    const showStimulus = () => {
      const gen = generationRef.current;
      setStage('stimulus');
      // Onset is stamped after paint, not when the render was scheduled.
      afterPaint(() => {
        if (generationRef.current !== gen) return;
        // For a chained task after the first trial, onset is the moment the
        // previous step's tap landed — not this paint. Nothing is repainted
        // between steps, so there is no new paint to stamp.
        const onsetPerf =
          chained && i > 0 && lastResponsePerfRef.current != null
            ? lastResponsePerfRef.current
            : clockRef.current.perfNow();
        const record = {
          trialIndex: trial.trialIndex,
          clientEventId: makeEventId(),
          answer: null,
          reactionTimeMs: null,
          stimulusAtEpochMs: clockRef.current.toEpoch(onsetPerf),
          respondedAtEpochMs: null,
        };
        // Appended now, so a non-response is already recorded as a
        // non-response. Nothing removes from this array.
        responsesRef.current.push(record);
        // index and timing are carried here so respond() can re-schedule the
        // end of this trial without them being in its own scope.
        activeRef.current = {
          record,
          onsetPerf,
          trial,
          accepted: false,
          // Deliberately separate from `accepted`. `accepted` means "a response
          // has landed"; this means "no response ever may". Collapsing the two
          // would make endTrial's practice-feedback branch treat a prime as a
          // miss.
          acceptsInput: acceptsResponse(task, trial),
          index: i,
          timing,
          endTimer: null,
          // Set by beginComposite once the first interaction of a composite
          // response has been stamped. commitComposite refuses to write an
          // answer without it — see both below.
          compositeOpen: false,
        };

        // By default the trial runs its full window, whether or not the user
        // responded, so go trials are not visibly shorter than no-go trials —
        // fixed pacing stops the user learning the rhythm rather than the rule.
        // A driver that opts into advanceOnResponseMs replaces this timer in
        // respond(), unless the task declares holdsFullWindow; see the comment
        // there.
        activeRef.current.endTimer = schedule(
          () => fns.current.endTrial(i, timing),
          timing.windowMs
        );
      });
    };

    const beginFixation = () => {
      // Practice feedback is cleared here rather than at the top of runTrial so
      // that it survives the gap and the fixation of the following trial. With
      // gapMs: 0 the old placement made feedback for a miss invisible, because
      // React batched the set and the clear into one render. A cued task clears
      // it at cue onset so old correctness never overlaps the next rule.
      //
      // But that only works when a TIMER separates the two. A trial that
      // continues the previous turn reaches here synchronously, so the response's
      // setFeedback and this clear land in one render and nothing ever paints —
      // which is the very failure the placement was meant to cure, reappearing
      // for the tasks that have no fixation to schedule. It made trail-making's
      // training feedback dead on every step but the last, and n-back's dead on
      // every missed match.
      //
      // Exempting those tasks is also right on its own terms: with no fixation
      // and no gap there is no blank for old correctness to sit in, so there is
      // nothing to clear it before. The feedback simply stands until the next
      // response replaces it — and on these instruments every trial produces
      // one. The practice→scored clear at the top of runTrial still applies, so
      // correctness never crosses that boundary.
      if (!continuesPreviousTurn) setFeedback(null);
      if (task.CueView && timing.cueMs > 0) {
        const gen = generationRef.current;
        setStage('cue');
        // The preparation interval is time the cue is actually visible, not
        // time since React was asked to render it. Starting this timer only
        // after paint gives every participant the full served interval.
        afterPaint(() => {
          if (generationRef.current !== gen) return;
          schedule(showStimulus, timing.cueMs);
        });
      } else {
        showStimulus();
      }
    };

    // A chained task's field is already on screen once its first trial has
    // painted, so from trial 1 onward there is nothing to wait for: no
    // fixation cross is drawn, and the onset showStimulus stamps for these
    // trials is already fixed to lastResponsePerfRef, not to whenever this
    // callback happens to run. Routing it through a scheduled timer would add
    // pure latency with nothing to measure, and would make the very next
    // step's response wait on an intervening timer tick that a continuously
    // displayed field has no reason to need. Trial 0 still schedules
    // normally (fixationMs is 0 here too, but going through the same path as
    // every other trial keeps its onset tied to an actual paint).
    // A zero-length fixation is routed synchronously for the same reason a
    // chained step is: there is nothing to wait for, and a zero-delay timer
    // only creates a render in which the stimulus is unmounted. Both paths
    // still stamp their onset from an actual paint inside showStimulus, so
    // neither shortens the measured interval.
    if (continuesPreviousTurn) {
      beginFixation();
    } else {
      schedule(beginFixation, fixationMs);
    }
  };

  fns.current.endTrial = (i, timing) => {
    const active = activeRef.current;
    rollbackOpenComposite(active);
    // A step that recorded nothing leaves lastResponsePerfRef pointing at the
    // tap that ended an EARLIER step. On a chained task the next step would
    // anchor its onset there and bill this whole elapsed window as part of the
    // next step's reaction time — and since that inflated value then exceeds
    // the task's own window-derived ceiling, respond()/beginComposite record it
    // as a non-response WITHOUT updating the ref, so the stale anchor survives
    // and the error compounds until the block ends. Checked after
    // rollbackOpenComposite so a begun-but-uncommitted composite counts as the
    // non-response it has just been rolled back to. This is the third place the
    // anchor must not survive, alongside a block boundary (startBlock), an
    // interruption (visibilitychange) and a preamble (runTrial); falling back
    // to the next step's own paint is the only honest anchor left.
    if (active && active.record.reactionTimeMs === null) lastResponsePerfRef.current = null;
    if (
      active &&
      active.acceptsInput &&
      !active.accepted &&
      feedbackAllowed(task, driver, active.trial)
    ) {
      const expected = task.practiceExpectation(
        active.trial.stimulus,
        expectationContext(blockRef.current, active.index)
      );
      // A non-response is correct only where withholding was the right move.
      setFeedback({ correct: expected === null });
    }
    activeRef.current = null;
    setStage('gap');
    const gap = task.chainOnset === true ? 0 : timing.gapMs;
    if (gap > 0) {
      schedule(() => fns.current.runTrial(i + 1), gap);
    } else {
      fns.current.runTrial(i + 1);
    }
  };

  fns.current.finishBlock = () => {
    setStage('gap');
    setPhaseTracked('submitting');
    // The index the SERVER gave us, never a client-side counter. The server
    // regenerates the block from its seed to grade it, so submitting under the
    // wrong index grades the participant against stimuli they never saw.
    fns.current.submit(blockRef.current.blockIndex, responsesRef.current, 0);
  };

  fns.current.submit = async (blockIndex, trials, attempt) => {
    try {
      const res = await driver.submitBlock(blockIndex, trials);
      // The unmount may have landed while this request was in flight. Without
      // this check handleSubmitted would call startBlock, which bumps the
      // generation and so re-arms the whole loop on a dead hook — running a
      // block the user never saw and submitting it as all non-responses.
      if (!aliveRef.current) return;
      fns.current.handleSubmitted(res);
    } catch (e) {
      if (!aliveRef.current) return;
      if (FATAL_STATUSES.includes(e?.status)) {
        setError(e);
        setPhaseTracked('error');
        return;
      }
      if (attempt < RETRY_DELAYS_MS.length) {
        schedule(
          () => fns.current.submit(blockIndex, trials, attempt + 1),
          RETRY_DELAYS_MS[attempt]
        );
        return;
      }
      setError(e);
      setPhaseTracked('submitFailed');
    }
  };

  fns.current.handleSubmitted = async (res) => {
    if (res.stats) setBlockStats((prev) => [...prev, res.stats]);
    if (res.sessionSummary != null) setSessionSummary(res.sessionSummary);

    if (res.complete || !res.nextBlock) {
      if (typeof driver.finalize === 'function') {
        setPhaseTracked('finalizing');
        try {
          const finalized = await driver.finalize();
          if (!aliveRef.current) return;
          setFinalResult(finalized);
        } catch (e) {
          if (!aliveRef.current) return;
          setError(e);
          setPhaseTracked('error');
          return;
        }
      }
      setPhaseTracked('complete');
      return;
    }
    fns.current.startBlock(res.nextBlock);
  };

  fns.current.startBlock = (blk) => {
    clearTimers();
    blockRef.current = blk;
    responsesRef.current = [];
    // A chained task must never carry an onset across a block boundary — the
    // new block's field hasn't rendered yet, so there is no continuity to
    // inherit from whatever the previous block's last response landed at.
    lastResponsePerfRef.current = null;
    setBlock(blk);
    setPhaseTracked('running');
    fns.current.runTrial(0);
  };

  const start = async () => {
    if (phaseRef.current !== 'idle' && phaseRef.current !== 'error') return;
    // start() is re-entrant from 'error', so everything the failed run left
    // behind is reset here. Otherwise a restart would accumulate its stats and
    // trial records on top of the abandoned ones.
    clearTimers();
    activeRef.current = null;
    responsesRef.current = [];
    setError(null);
    setBlockStats([]);
    setFinalResult(null);
    setSessionSummary(null);
    setFeedback(null);
    setTrialIndex(-1);
    setPhaseTracked('starting');
    clockRef.current = createClock();
    try {
      const res = await driver.start();
      if (!aliveRef.current) return;
      setBlockCount(res.blockCount);
      fns.current.startBlock(res.block);
    } catch (e) {
      if (!aliveRef.current) return;
      setError(e);
      setPhaseTracked('error');
    }
  };

  const respond = (targetId) => {
    const active = activeRef.current;
    if (!active || active.accepted || !active.acceptsInput) return;

    const rt = Math.round(clockRef.current.perfNow() - active.onsetPerf);
    if (rt < MIN_RT_MS) return; // anticipation — the trial stays open

    if (rt > maxRtMsFor(active)) {
      // Closed without feedback, deliberately. Leaving `accepted` unset here so
      // endTrial's feedback branch could still fire would mean reopening the
      // trio-write path to a press we already decided not to record — the one
      // thing this file guarantees never happens is a write outside respond()'s
      // single site. A learner on tower-of-london or corsi-span who answers past
      // the ceiling sees the screen simply move on, on those two instruments
      // only. Cosmetic; not a bug.
      active.accepted = true;      // no second chance at this trial
      setSelectedTargetId(null);   // nothing to highlight; nothing was recorded
      return;                      // record stays all-null
    }

    active.accepted = true;
    // Read by the NEXT trial's onset if the task chains. Set only on a
    // response that actually landed and was recorded — not on the
    // ceiling bail-out above, which is a non-response by design.
    lastResponsePerfRef.current = clockRef.current.perfNow();
    setSelectedTargetId(targetId);
    // All three written together, in one place. The server mirrors a database
    // CHECK constraint here: a partially populated trio is a 400, not a lenient
    // degrade. There must be no code path that sets one without the others.
    active.record.answer = task.toAnswer(targetId);
    active.record.reactionTimeMs = rt;
    active.record.respondedAtEpochMs = active.record.stimulusAtEpochMs + rt;

    if (feedbackAllowed(task, driver, active.trial)) {
      const expected = task.practiceExpectation(
        active.trial.stimulus,
        expectationContext(blockRef.current, active.index)
      );
      setFeedback({ correct: expected === targetId });
    }

    // A driver opts into ending the trial shortly after an accepted response
    // instead of waiting out the rest of the window, and a TASK may veto it.
    //
    // The veto is the whole of the fixed-pacing argument, and it is a property
    // of the paradigm rather than of the mode. Where withholding is itself a
    // correct response — go-no-go, n-back — ending on the press makes a go
    // trial visibly shorter than a no-go trial, and the rule becomes readable
    // from the pacing alone; where the window IS the served isi_ms — n-back,
    // speed-match — cutting it short removes the manipulation the difficulty
    // ladder is escalating. Those three declare holdsFullWindow and are
    // unaffected by any driver, in either mode.
    //
    // On the other fifteen every trial ends in a response, so trial length
    // carries no information and there is nothing to protect — only dead time
    // to avoid, up to twenty seconds of it per item on matrix-reasoning.
    //
    // This runs only past the two guards above, so an anticipation
    // (rt < MIN_RT_MS) and a second press never reach it.
    const advanceMs = task.holdsFullWindow === true ? null : driver.advanceOnResponseMs;
    if (typeof advanceMs === 'number' && advanceMs > 0) {
      clearTimeout(active.endTimer);
      active.endTimer = schedule(
        () => fns.current.endTrial(active.index, active.timing),
        advanceMs
      );
    }
  };

  // Composite tasks build an answer from many interactions — a tapped sequence,
  // a list of moves. Reaction time is stamped at the FIRST interaction, not at
  // the commit: the backend defines it as planning latency on tower-of-london
  // and as time-to-first-tap on corsi-span, and explicitly says not to send
  // total solution time. Stamping it here is also what keeps the response trio
  // coherent — no first interaction means no RT means an all-null record, which
  // is a valid omission rather than a partially populated 400.
  const beginComposite = () => {
    const active = activeRef.current;
    if (!active || !active.acceptsInput || active.accepted) return;
    if (active.record.reactionTimeMs !== null) return; // already stamped

    const rt = Math.round(clockRef.current.perfNow() - active.onsetPerf);
    if (rt < MIN_RT_MS) return;  // anticipation — the trial stays open
    if (rt > maxRtMsFor(active)) {
      // Past the task's own window (plus grace). Recorded as a non-response
      // rather than as a 400 that would lose the whole block. corsi-span's
      // 20s window makes this the first interaction of a multi-tap sequence,
      // so the same window-derived ceiling applies here as in respond().
      active.accepted = true;
      return;
    }
    active.record.reactionTimeMs = rt;
    active.record.respondedAtEpochMs = active.record.stimulusAtEpochMs + rt;
    // `answer` is deliberately still null here. commitComposite writes it, and
    // until it does this record is INCOMPLETE — see commitComposite's guard.
    active.compositeOpen = true;
  };

  // Ends the trial. `answer` must be a non-null object; a composite task that
  // wants to report "nothing was done" simply never calls beginComposite.
  const commitComposite = (answer) => {
    const active = activeRef.current;
    if (!active || !active.acceptsInput || active.accepted) return;
    if (!active.compositeOpen) return;      // no first interaction was ever stamped
    if (answer == null) return;             // would produce a partial trio

    active.accepted = true;
    // Read by the NEXT trial's onset if the task chains — see the matching
    // comment in respond().
    lastResponsePerfRef.current = clockRef.current.perfNow();
    active.record.answer = answer;

    if (feedbackAllowed(task, driver, active.trial)) {
      const expected = task.practiceExpectation(
        active.trial.stimulus,
        expectationContext(blockRef.current, active.index)
      );
      // A composite task's expectation is compared against the answer object,
      // not against a targetId. Tasks that cannot compute it set
      // feedbackAvailable: false and never reach here.
      setFeedback({ correct: expected !== null && JSON.stringify(expected) === JSON.stringify(answer) });
    }

    clearTimeout(active.endTimer);
    fns.current.endTrial(active.index, active.timing);
  };

  // Only a run that exhausted its retries may be retried by hand. From
  // 'submitting' this would open a second concurrent submit, and from 'error' it
  // would resend the payload a 400 already rejected.
  const retry = () => {
    if (phaseRef.current !== 'submitFailed') return;
    setError(null);
    setPhaseTracked('submitting');
    fns.current.submit(blockRef.current.blockIndex, responsesRef.current, 0);
  };

  // Only a paused run may be resumed. From any other phase this would start a
  // second loop alongside the one already running, and the two would interleave
  // trial records into the same block.
  //
  // Records are pushed at stimulus onset, so the length of the array is exactly
  // the number of trials that actually played. Resuming from the last trial
  // *index* instead would skip a trial that was interrupted during its fixation
  // delay, since that trial has no record yet, and the block would submit short.
  const resume = () => {
    if (phaseRef.current !== 'paused') return;
    setPhaseTracked('running');
    fns.current.runTrial(responsesRef.current.length);
  };

  const abandon = async () => {
    clearTimers();
    // No submit follows abandonment today, so this is defensive rather than
    // observably load-bearing — but it is the same "let go of a live trial"
    // shape as endTrial and the visibility handler, and leaving it out here
    // would make the guarantee true only by accident of what currently calls
    // this function.
    rollbackOpenComposite(activeRef.current);
    activeRef.current = null;
    // Back to idle BEFORE the await, and regardless of what the network does.
    // Callers do not all unmount the hook — GScoreAssessmentPage abandons and
    // merely hides the shell when the server picks an instrument this build
    // cannot render — and start() proceeds only from 'idle' or 'error'. Leaving
    // the phase at 'running' therefore stranded the runner permanently: every
    // later attempt to begin flashed the shell for one render and no-oped, and
    // only a page reload recovered. start() resets everything else it needs, so
    // the phase is the whole of the reset here.
    setPhaseTracked('idle');
    try {
      await driver.abandon();
    } catch {
      // Abandoning is best-effort; the user is leaving either way.
    }
  };

  // Freeze on tab hide and resume on return. Trials already played are
  // untouched, and resume() restarts from responsesRef.current.length, so a
  // trial interrupted before its stimulus appeared is replayed rather than
  // skipped and the block still submits its full trial count. Known cost: a
  // trial already on screen at the moment of hiding records as a miss, because
  // the payload has no field to express abandonment. A composite trial that
  // had begun (RT stamped, answer still null) gets the same rollback endTrial
  // applies — this is the second of two places `active` can be dropped from a
  // live trial without going through endTrial, and resuming must not resubmit
  // a half-populated record.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && phaseRef.current === 'running') {
        clearTimers();
        rollbackOpenComposite(activeRef.current);
        activeRef.current = null;
        // A chained task's next onset would otherwise still be anchored to
        // whatever tap landed before the tab hid — resume() calls runTrial()
        // directly (not startBlock(), which is the only other place this
        // ref is cleared), so without this the away-time reads as reaction
        // time on the first resumed step. Falling back to
        // clockRef.current.perfNow() (the resumed step's own paint) is the
        // only honest anchor left after an interruption.
        lastResponsePerfRef.current = null;
        setPhaseTracked('paused');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Liveness is set in the effect body, not just at ref creation, so that
  // StrictMode's mount/unmount/remount of the same hook instance revives it
  // rather than leaving it permanently dead.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      clearTimers();
    };
  }, []);

  return {
    phase,
    stage,
    block,
    blockCount,
    trialIndex,
    blockStats,
    finalResult,
    sessionSummary,
    feedback,
    selectedTargetId,
    error,
    preamblePhase,
    start,
    respond,
    beginComposite,
    commitComposite,
    retry,
    resume,
    abandon,
  };
}
